import { useState } from "react";

import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

import { IProductionManagerBay } from "../../models/ProductionManager";
import { IPlannerVehicleRow } from "../../models/ProductionPlanner";
import classNames from "../../utils/classNames";
import { complexityChipClasses, complexityLabel } from "../../utils/vehicleDisplay";

interface DueNextSectionProps {
  bays: IProductionManagerBay[];
}

interface QueueEntry {
  bayName: string;
  vehicle: IPlannerVehicleRow;
}

interface QueueGroupProps {
  title: string;
  dotClass: string;
  entries: QueueEntry[];
}

function QueueGroup({ title, dotClass, entries }: QueueGroupProps) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? entries : entries.slice(0, 1);
  const remaining = entries.length - 1;

  return (
    <div className="flex-1 bg-white border border-gray-200 rounded-md p-3 min-w-[280px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <span className={classNames("h-2 w-2 rounded-full", dotClass)} />
          {title}
        </div>
        {entries.length > 1 && (
          <button
            type="button"
            onClick={() => setExpanded((p) => !p)}
            className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
          >
            {expanded ? (
              <>
                Show less
                <ChevronUpIcon className="h-3.5 w-3.5" />
              </>
            ) : (
              <>
                Show {remaining} more
                <ChevronDownIcon className="h-3.5 w-3.5" />
              </>
            )}
            <span className="ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded bg-gray-100 px-1 text-[10px] font-semibold text-gray-700">
              {entries.length}
            </span>
          </button>
        )}
      </div>

      {entries.length === 0 ? (
        <div className="mt-3 text-xs text-gray-400 italic">Nothing queued</div>
      ) : (
        <div className="mt-2 divide-y divide-gray-100">
          <div className="pb-1 text-[10px] uppercase tracking-wide text-gray-400">
            ↑ Onto the line
          </div>
          {visible.map((entry, idx) => (
            <div
              key={`${entry.bayName}-${entry.vehicle.id}`}
              className="py-2 flex items-start gap-3"
            >
              <div className="text-xs text-gray-400 mt-0.5 w-4 shrink-0 text-right">
                {idx + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 truncate">
                    {entry.vehicle.vrm || "No VRM"}
                  </span>
                  {idx === 0 && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-gray-900 text-white">
                      Next
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-600 truncate">
                  {[entry.vehicle.combinedMake, entry.vehicle.combinedModel]
                    .filter(Boolean)
                    .join(" ")}
                  {entry.vehicle.bodyStyle && (
                    <span className="ml-1 text-gray-500">{entry.vehicle.bodyStyle}</span>
                  )}
                </div>
                <div className="text-[11px] text-gray-500">{entry.bayName}</div>
              </div>
              <span
                className={classNames(
                  "text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 shrink-0",
                  complexityChipClasses(entry.vehicle.complexity)
                )}
              >
                {complexityLabel(entry.vehicle.complexity)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DueNextSection({ bays }: DueNextSectionProps) {
  return (
    <div className="flex gap-3 flex-wrap">
      {bays.map((bay) => (
        <QueueGroup
          key={bay.id}
          title={bay.name}
          dotClass="bg-gray-900"
          entries={(bay.dueNext ?? []).map((v) => ({ bayName: bay.name, vehicle: v }))}
        />
      ))}
    </div>
  );
}
